// SideNav.tsx — the desktop/web left navigation rail (replaces the bottom tab
// bar on wide screens). Web-only: only rendered by AppShell when useIsDesktop().
import { type CSSProperties } from 'react';
import { Wordmark, Icon, type IconName } from './icons';
import { ACTIVE_TABS, isTabLocked, tabLabel, type TabId } from './TabBar';

// Journey now lives in the shared tab list (next to Today), so the rail just follows them.
const NAV_ITEMS: { id: TabId; label: string; icon: IconName }[] = [...ACTIVE_TABS];

function navItemStyle(active: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 13,
    width: '100%',
    padding: '11px 14px',
    borderRadius: 14,
    border: active ? '1px solid var(--hair)' : '1px solid transparent',
    cursor: 'pointer',
    textAlign: 'left',
    background: active ? 'linear-gradient(180deg, var(--card-top), var(--card-bot))' : 'transparent',
    boxShadow: active ? 'var(--edge), var(--sh1)' : 'none',
    // Inactive items now use full ink (not ink-soft) so the rail reads clearly.
    color: active ? 'var(--amber-deep)' : 'var(--ink)',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 14.5,
    fontWeight: active ? 700 : 600,
    minHeight: 46,
  };
}

// "Get help" is support + crisis signposting, so it gets a distinct, always-obvious
// treatment (warm terracotta) — never blends in with the ordinary nav items.
const helpItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 14px',
  borderRadius: 14,
  border: '1px solid rgba(156,61,44,0.30)',
  cursor: 'pointer',
  textAlign: 'left',
  background: 'rgba(156,61,44,0.09)',
  color: '#9C3D2C',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  fontWeight: 700,
  minHeight: 48,
  boxShadow: '0 1px 3px rgba(156,61,44,0.12)',
};

export function SideNav({
  tab,
  onTab,
  onOpenSettings,
  onOpenProgress,
  onInspire,
  onOpenHelp,
  locked = false,
  onLocked,
}: {
  locked?: boolean;
  onLocked?: () => void;
  tab: TabId;
  onTab: (id: TabId) => void;
  onOpenSettings: () => void;
  onOpenProgress: () => void;
  onInspire: () => void;
  onOpenHelp: () => void;
}) {
  return (
    <nav
      aria-label="Primary"
      style={{
        width: 250,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 16px calc(20px + var(--safe-bottom))',
        // A clearly raised panel (not just a translucent wash) so the rail reads as
        // navigation, distinct from the cream content area.
        background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
        borderRight: '1px solid var(--hair)',
        boxShadow: '1px 0 0 var(--hair), 8px 0 24px -16px rgba(60,40,15,0.25)',
      }}
    >
      <div style={{ padding: '2px 12px 24px' }}>
        <Wordmark h={20} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {NAV_ITEMS.map((t) => {
          const on = tab === t.id;
          const lockedTab = isTabLocked(t.id, locked);
          return (
            <button
              key={t.id}
              onClick={() => (lockedTab ? onLocked?.() : onTab(t.id))}
              aria-current={on ? 'page' : undefined}
              aria-label={lockedTab ? tabLabel(t.label, true) : undefined}
              style={lockedTab ? { ...navItemStyle(on), opacity: 0.5, filter: 'saturate(0.3)' } : navItemStyle(on)}
            >
              <Icon name={t.icon} size={20} sw={on ? 1.9 : 1.6} />
              {t.label}
              {lockedTab && (
                <span aria-hidden style={{ display: 'flex', marginLeft: 'auto' }}>
                  <Icon name="lock" size={15} sw={2} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ height: 1, background: 'var(--hair)', margin: '8px 6px 12px' }} />
      <button onClick={onOpenHelp} aria-label="Get help — support lines and your organisation" style={helpItemStyle}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="4" />
          <path d="M4.9 4.9l4.2 4.2M14.9 14.9l4.2 4.2M14.9 9.1l4.2-4.2M4.9 19.1l4.2-4.2" />
        </svg>
        Get help
      </button>
      <div style={{ height: 6 }} />
      <button onClick={onOpenProgress} aria-label="Your recovery" style={navItemStyle(false)}>
        <Icon name="chart" size={20} />
        Your Recovery
      </button>
      <button onClick={onInspire} aria-label="Inspire me — with the words of others" style={navItemStyle(false)}>
        <Icon name="sparkle" size={20} />
        Inspire me
      </button>
      <button onClick={onOpenSettings} aria-label="Settings" style={navItemStyle(false)}>
        <Icon name="settings" size={20} />
        Settings
      </button>
    </nav>
  );
}
